// src/components/home/VibeFeaturesSection.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Brain, Target, TrendingUp, Heart, Shield, Compass, Zap, Star, Sparkles, Award } from 'lucide-react';
import Card from '../shared/Card';

const VibeFeaturesSection = () => {
  const vibeFeatures = [
    {
      icon: Brain,
      title: 'Your Personal Vibe Check',
      description: '20 questions that actually matter - discover your unique self-talk patterns and what they mean for your life.',
      color: 'from-purple-500 to-indigo-500',
      emoji: '🧠',
      vibe: 'Know thyself, but make it fun',
    },
    {
      icon: Target,
      title: 'Custom Game Plan', 
      description: "Get recommendations designed specifically for YOUR brain, not generic advice that doesn't fit your life.",
      color: 'from-pink-500 to-purple-500',
      emoji: '🎯',
      vibe: 'Personalised like your Spotify wrapped',
    },
    {
      icon: TrendingUp,
      title: 'Level Up Your Progress',
      description: 'Track your inner voice transformation with XP, achievements, and insights that actually show your growth.',
      color: 'from-green-500 to-teal-500',
      emoji: '📈',
      vibe: 'Gamify your glow-up',
    },
    {
      icon: Heart,
      title: 'Daily Confidence Boosts', 
      description: 'Micro-practices and affirmations that build genuine self-love, not just positive thinking BS.',
      color: 'from-red-500 to-pink-500',
      emoji: '❤️',
      vibe: "Self-love that's actually sustainable",
    },
    {
      icon: Shield,
      title: 'Your Safe Space',
      description: 'Everything stays private on your device. No judgment, no data selling, just you and your growth.',
      color: 'from-blue-500 to-cyan-500',
      emoji: '🛡️',
      vibe: 'Privacy like it should be',
    },
    {
      icon: Sparkles,
      title: 'Science That Makes Sense',
      description: "Real psychology research explained in a way that's actually useful for your daily life, not academic jargon.",
      color: 'from-yellow-500 to-orange-500',
      emoji: '✨',
      vibe: 'Smart content, zero boring',
    },
  ];
  
  return (
<section className="py-20 bg-gradient-to-br from-slate-800/50 to-purple-900/30 relative">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="inline-block mb-4"
          >
            <div className="text-6xl mb-4">🚀</div>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Why This Actually Works
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            We ditched the boring self-help formula and built something that actually fits into your life. 
            <span className="text-purple-300 font-semibold"> Real tools for real change.</span>
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {vibeFeatures.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              className="h-full"
            >
              <Card className="p-6 h-full hover-lift bg-gradient-to-br from-white/5 to-white/10 border-purple-500/20 relative overflow-hidden">
                {/* Background decoration */}
                <div className="absolute top-0 right-0 w-20 h-20 opacity-10">
                  <div className={`w-full h-full rounded-full bg-gradient-to-br ${feature.color}`} />
                </div>
                
                <div className="relative space-y-4">
                  {/* Icon with gradient */}
                  <div className="flex items-center gap-3 mb-4">
                    <motion.div 
                      className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.color} flex items-center justify-center shadow-lg`}
                      whileHover={{ rotate: 5, scale: 1.1 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <feature.icon className="w-7 h-7 text-white" />
                    </motion.div>
                    <span className="text-3xl">{feature.emoji}</span>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {feature.title}
                    </h3>
                    
                    <p className="text-slate-300 mb-4 leading-relaxed">
                      {feature.description}
                    </p>
                    
                    <div className="flex items-center gap-2">
                      <Zap className="w-4 h-4 text-yellow-400" />
                      <span className="text-purple-300 text-sm font-medium italic">
                        {feature.vibe}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Bottom stats row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
        >
          {[
            { stat: '20', label: 'Assessment Questions', subtext: 'Deep but not overwhelming' },
            { stat: '8', label: 'Core Modules', subtext: 'Bite-sized transformation' },
            { stat: '100+', label: 'Real Techniques', subtext: 'Actually proven to work' },
            { stat: '∞', label: 'Your Potential', subtext: "Sky's the limit" },
          ].map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.9 + index * 0.1 }}
              className="group"
            >
              <div className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-indigo-400 mb-2 group-hover:from-pink-400 group-hover:to-purple-400 transition-all">
                {item.stat}
              </div>
              <div className="text-white font-semibold mb-1">
                {item.label}
              </div>
              <div className="text-slate-400 text-sm">
                {item.subtext}
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Call to action */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 1.2 }}
          className="text-center mt-16"
        >
          <div className="bg-gradient-to-r from-purple-900/40 to-indigo-900/40 border border-purple-500/30 rounded-2xl p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Ready to Transform Your Inner Voice? ✨
            </h3>
            <p className="text-purple-200 mb-6">
              Be one of the first to pioneer this journey to more confident, resilient thinking.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <span>Free to start</span>
              </div>
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-green-400" />
                <span>Completely private</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-purple-400" />
                <span>Evidence-based</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default VibeFeaturesSection;